/**
 * The pure view logic for /scores?teamId=: the tab set, the active tab rule,
 * and the header meta line.
 *
 * WHAT THIS FILE IS ALLOWED TO DECIDE, WHICH IS LESS THAN IT LOOKS. The server
 * owns the tab list. It sends what the league stores, narrowed to what this
 * team can fill, and `visibleTeamTabs` below adds nothing and reorders nothing.
 * The only judgment left here is whether a renderer exists.
 *
 * Renderers exist for Overview, Schedule, Roster, Player stats, Team stats
 * and Injuries. Player stats reads sports-api POST /team/player-stats-get and
 * is offered only to a team with stored season lines (ISSUE-1384); the
 * `stats` key reads "Team stats" so the two never read alike. Roster
 * reads sports-api POST /team/roster-get and is offered only to a team with
 * stored roster rows (ISSUE-1168). Injuries reads
 * sports-api POST /team/injuries-get, filled daily by the goalserve ingest
 * since ISSUE-984, offered only for leagues whose `hasInjuryReport` is true
 * (NFL, NBA, MLB and NHL).
 *
 * Six strings below are planner authored because no design document covers
 * those states: TEAM_NO_RECORD_LABEL, TEAM_PROFILE_MISSING_TITLE,
 * TEAM_PROFILE_MISSING_BODY, TEAM_TABS_ARIA_LABEL, and ISSUE-1021's
 * TEAM_PROFILE_FAILED_LINE and TEAM_SCHEDULE_FAILED_LINE.
 */

export const TEAM_TAB_KEYS = [
  "overview",
  "schedule",
  "roster",
  "playerStats",
  "stats",
  "injuries",
] as const;
export type typeTeamTabKey = (typeof TEAM_TAB_KEYS)[number];

export const TEAM_TAB_LABELS: Record<typeTeamTabKey, string> = {
  overview: "Overview",
  schedule: "Schedule",
  roster: "Roster",
  playerStats: "Player stats",
  stats: "Team stats",
  injuries: "Injuries",
};

export const TEAM_TABS_ARIA_LABEL = "Team profile";
export const TEAM_META_SEPARATOR = " · ";
export const TEAM_NO_RECORD_LABEL = "No record yet";
export const TEAM_PROFILE_MISSING_TITLE = "Team not found";
export const TEAM_PROFILE_MISSING_BODY = "This team isn't on Sportsmet yet.";
export const TEAM_PROFILE_FAILED_LINE = "We could not load this team.";
export const TEAM_SCHEDULE_FAILED_LINE =
  "We could not load this team's schedule.";

/**
 * Keeps the server's order, drops any value outside TEAM_TAB_KEYS, and
 * collapses duplicates (first occurrence wins). When nothing survives,
 * returns ["overview"]: a screen with no tab strip and no panel is worse
 * than one tab.
 */
export function visibleTeamTabs(
  serverTabs: readonly string[],
): typeTeamTabKey[] {
  const seen = new Set<typeTeamTabKey>();
  for (const tab of serverTabs) {
    if ((TEAM_TAB_KEYS as readonly string[]).includes(tab)) {
      seen.add(tab as typeTeamTabKey);
    }
  }
  const result = [...seen];
  return result.length === 0 ? ["overview"] : result;
}

/**
 * Returns the requested tab when it is offered, otherwise the first offered
 * tab, otherwise "overview".
 */
export function resolveActiveTeamTab(
  visible: readonly typeTeamTabKey[],
  requested: string | undefined,
): typeTeamTabKey {
  if (
    requested !== undefined &&
    (visible as readonly string[]).includes(requested)
  ) {
    return requested as typeTeamTabKey;
  }
  return visible[0] ?? "overview";
}

/**
 * Both server strings are rendered verbatim, joined by TEAM_META_SEPARATOR.
 * An empty string is treated the same as null.
 */
export function teamMetaLine(input: {
  recordLabel: string | null;
  standingLabel: string | null;
}): string {
  const record =
    input.recordLabel !== null && input.recordLabel !== ""
      ? input.recordLabel
      : null;
  const standing =
    input.standingLabel !== null && input.standingLabel !== ""
      ? input.standingLabel
      : null;
  if (record !== null && standing !== null) {
    return `${record}${TEAM_META_SEPARATOR}${standing}`;
  }
  if (record !== null) {
    return record;
  }
  if (standing !== null) {
    return standing;
  }
  return TEAM_NO_RECORD_LABEL;
}

/** Every `/scores` pane returns to the Scores section it was opened from. */
export const TEAM_PROFILE_BACK_LABEL = "Back to Scores";

export type typeTeamProfilePhase = "loading" | "missing" | "failed" | "ready";

/**
 * Returns `missing` only for a `NOT_FOUND` answer. A failed read is never a
 * missing team (ISSUE-1021). Data already held wins over a failed background
 * refetch.
 */
export function resolveTeamProfilePhase(input: {
  isPending: boolean;
  isError: boolean;
  errorCode: string | null;
  hasData: boolean;
}): typeTeamProfilePhase {
  if (input.hasData) return "ready";
  if (input.errorCode === "NOT_FOUND") return "missing";
  if (input.errorCode !== null || input.isError) return "failed";
  if (input.isPending) return "loading";
  return "failed";
}

export type typeTeamSchedulePhase = "none" | "loading" | "failed" | "ready";

/**
 * `none` means no schedule was asked for. A failed read is never an empty
 * schedule (ISSUE-1021). A disabled query reports pending forever, so the
 * enabled check comes first.
 */
export function resolveTeamSchedulePhase(input: {
  isEnabled: boolean;
  isPending: boolean;
  isError: boolean;
  errorCode: string | null;
  hasData: boolean;
}): typeTeamSchedulePhase {
  if (!input.isEnabled) return "none";
  if (input.hasData) return "ready";
  if (input.errorCode !== null || input.isError) return "failed";
  if (input.isPending) return "loading";
  return "failed";
}
